"use client";
import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import {
   HandHelping,
   LogOut,
   ShieldCheck,
   User,
   Settings as SettingsIcon
} from "lucide-react";
import { Button } from "./button";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
   DropdownMenuLabel,
   DropdownMenuSeparator
} from "./dropdown-menu";
import { useAuth } from "@/app/hooks";
import { UserSettingsDialog } from "./UserSettingsDialog";

export function ProfileMenu() {
   const router = useRouter();
   const translation = useTranslations("Profile.menu");
   const { user, logout } = useAuth();
   const [settingsOpen, setSettingsOpen] = useState(false);

   const menuItem = useMemo(
      () => ({
         firstGroup: [
            {
               icon: <ShieldCheck strokeWidth={1.5} size={18} />,
               label: "admin_dashboard",
               className: user?.isAdmin ? "" : "hidden",
               onClick: () => {
                  router.push("/auth/admin");
               }
            },
            {
               icon: <User strokeWidth={1.5} size={18} />,
               label: "profile",
               onClick: () => {
                  router.push(`/profile/${user?.id}`);
               }
            }
         ],
         secondGroup: [
            {
               icon: <SettingsIcon strokeWidth={1.5} size={18} />,
               label: "settings",
               onClick: () => {
                  setSettingsOpen(true);
               }
            },
            {
               icon: <HandHelping strokeWidth={1.5} size={20} />,
               label: "help",
               onClick: () => {
                  router.push("/help");
               }
            },
            {
               icon: <LogOut strokeWidth={1.5} size={18} />,
               label: "logout",
               className: "hover:!text-red-500",
               onClick: () => logout()
            }
         ]
      }),
      [user?.isAdmin, user?.id, router, logout]
   );

   if (!user) return <></>;
   return (
      <>
         <DropdownMenu>
            <DropdownMenuTrigger asChild>
               <Button
                  variant="ghost"
                  size="sm"
                  className="relative flex rounded-full items-center gap-2 px-2 hover:bg-gray-200 dark:hover:bg-gray-700"
               >
                  {user?.avatarUrl ? (
                     <Avatar>
                        <AvatarImage src={user.avatarUrl} alt="tai-zen-ai" />
                        <AvatarFallback>{user?.username?.split("")?.[0]}</AvatarFallback>
                     </Avatar>
                  ) : (
                     <User className="h-5 w-5" />
                  )}
               </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
               <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                     <p className="text-sm font-medium leading-none">
                        {user?.metaData?.display_name ||
                           user?.username ||
                           translation("guest")}
                     </p>
                     <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                     </p>
                  </div>
               </DropdownMenuLabel>
               <DropdownMenuSeparator />
               {menuItem.firstGroup.map((item, index) => (
                  <DropdownMenuItem
                     key={index}
                     onClick={item.onClick}
                     className={item.className + " cursor-pointer"}
                  >
                     <div className="flex items-center gap-2">
                        {item.icon}
                        <span>{translation(item.label)}</span>
                     </div>
                  </DropdownMenuItem>
               ))}
               <DropdownMenuSeparator />
               {menuItem.secondGroup.map((item, index) => (
                  <DropdownMenuItem
                     key={index}
                     onClick={item.onClick}
                     className={item.className + " cursor-pointer"}
                  >
                     <div className="flex items-center gap-2">
                        {item.icon}
                        <span>{translation(item.label)}</span>
                     </div>
                  </DropdownMenuItem>
               ))}
            </DropdownMenuContent>
         </DropdownMenu>

         <UserSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      </>
   );
}
